/**
 * GA4 analytics layer.
 * Consent-gated; never throws; no PII / content IDs / raw durations.
 */

const GA_MEASUREMENT_ID = 'G-W9GB806WJJ';
const CONSENT_STORAGE_KEY = 'procrastiq.cookieConsent';
const GA_CONTENT_VIEW_PREFIX = 'ga_content_view_';

export type CookieConsent = 'accepted' | 'rejected';

export type AuthMethod = 'email' | 'google';

export type ContentType =
    | 'brain_snack'
    | 'attention'
    | 'logic'
    | 'knowledge'
    | 'language';

export type ContentSource =
    | 'recommended_feed'
    | 'category_feed'
    | 'direct'
    | 'search'
    | 'bookmark'
    | 'review'
    | 'shared_link';

export type ContentDifficultyLabel = 'easy' | 'medium' | 'hard';

export type DurationBucket =
    | 'under_5s'
    | '5_10s'
    | '10_30s'
    | '30_60s'
    | 'over_60s';

export type ShareMethod = 'copy_link' | 'native_share' | 'facebook' | 'x' | 'other';

export type ReactionType = 'like' | 'dislike';

export const CONTENT_TYPES: readonly ContentType[] = [
    'brain_snack',
    'attention',
    'logic',
    'knowledge',
    'language',
] as const;

export const CONTENT_SOURCES: readonly ContentSource[] = [
    'recommended_feed',
    'category_feed',
    'direct',
    'search',
    'bookmark',
    'review',
    'shared_link',
] as const;

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag: (...args: unknown[]) => void;
    }
}

let gtagLoaded = false;

/** Engagement clock per content id — started on content_view, consumed on complete. */
const engagementStartedAt = new Map<number, number>();

type GaParams = Record<string, string | number | boolean | undefined | null>;

// --- Consent / bootstrap -----------------------------------------------------

export function getCookieConsent(): CookieConsent | null {
    try {
        const value = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (value === 'accepted' || value === 'rejected') {
            return value;
        }
    } catch {
        // ignore
    }
    return null;
}

export function setCookieConsent(value: CookieConsent): void {
    try {
        localStorage.setItem(CONSENT_STORAGE_KEY, value);
    } catch {
        // ignore
    }
    if (value === 'accepted') {
        ensureGoogleAnalytics();
        trackPageView(window.location.pathname + window.location.search);
    }
}

export function initAnalyticsFromConsent(): void {
    if (getCookieConsent() === 'accepted') {
        ensureGoogleAnalytics();
    }
}

function ensureGoogleAnalytics(): void {
    if (gtagLoaded || typeof window === 'undefined') {
        return;
    }
    gtagLoaded = true;

    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', GA_MEASUREMENT_ID, {
        anonymize_ip: true,
        send_page_view: false,
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
}

// --- Core helpers ------------------------------------------------------------

function sanitizeParams(params?: GaParams): Record<string, string | number | boolean> {
    if (!params) return {};
    const out: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') continue;
        out[key] = value;
    }
    return out;
}

/** Generic GA4 event. No-ops without consent / gtag; never throws. */
export function trackEvent(name: string, params?: GaParams): void {
    try {
        if (getCookieConsent() !== 'accepted' || typeof window === 'undefined') {
            return;
        }
        ensureGoogleAnalytics();
        if (typeof window.gtag !== 'function') {
            return;
        }
        window.gtag('event', name, sanitizeParams(params));
    } catch {
        // Analytics must never break the app
    }
}

export function trackPageView(path: string): void {
    trackEvent('page_view', {
        page_path: path,
        page_location: typeof window !== 'undefined' ? window.location.href : undefined,
        page_title: typeof document !== 'undefined' ? document.title : undefined,
    });
}

export function toDurationBucket(durationMs: number): DurationBucket {
    if (durationMs < 5_000) return 'under_5s';
    if (durationMs < 10_000) return '5_10s';
    if (durationMs < 30_000) return '10_30s';
    if (durationMs < 60_000) return '30_60s';
    return 'over_60s';
}

export function toDifficultyLabel(difficulty: number): ContentDifficultyLabel {
    if (difficulty <= 2) return 'easy';
    if (difficulty === 3) return 'medium';
    return 'hard';
}

const CATEGORY_TO_CONTENT_TYPE: Record<string, ContentType> = {
    'brain-logic': 'logic',
    'logic-puzzles': 'logic',
    'languages-words': 'language',
    'language-curiosities': 'language',
    'human-psychology': 'attention',
    'world': 'knowledge',
    'science-discoveries': 'knowledge',
    'history-civilizations': 'knowledge',
    'technology-future': 'knowledge',
    'money-economics': 'knowledge',
    'culture-media': 'knowledge',
    'sports-games': 'knowledge',
};

const TAG_TO_CONTENT_TYPE: Record<string, ContentType> = {
    logic: 'logic',
    numbers: 'logic',
    deduction: 'logic',
    spatial: 'logic',
    estimation: 'logic',
    probability: 'logic',
    'brain-teaser': 'brain_snack',
    curiosities: 'brain_snack',
    language: 'language',
    psychology: 'attention',
    geography: 'knowledge',
    history: 'knowledge',
    science: 'knowledge',
    myth: 'knowledge',
    comparison: 'knowledge',
    sport: 'knowledge',
    food: 'knowledge',
    culture: 'knowledge',
    technology: 'knowledge',
    economics: 'knowledge',
};

export function resolveContentType(input: {
    category?: string | null;
    subcategory?: string | null;
    tagSlugs?: Array<string | null | undefined> | null;
}): ContentType {
    const category = (input.category || '').trim().toLowerCase();
    if (category && CATEGORY_TO_CONTENT_TYPE[category]) {
        return CATEGORY_TO_CONTENT_TYPE[category];
    }
    const subcategory = (input.subcategory || '').trim().toLowerCase();
    if (subcategory && CATEGORY_TO_CONTENT_TYPE[subcategory]) {
        return CATEGORY_TO_CONTENT_TYPE[subcategory];
    }
    for (const raw of input.tagSlugs ?? []) {
        const slug = (raw || '').trim().toLowerCase();
        if (slug && TAG_TO_CONTENT_TYPE[slug]) {
            return TAG_TO_CONTENT_TYPE[slug];
        }
    }
    return 'knowledge';
}

export function resolveContentSource(hints?: {
    hasCategoryFilter?: boolean;
    fromSeedSlug?: boolean;
    isBookmarks?: boolean;
    isReview?: boolean;
    isSearch?: boolean;
}): ContentSource {
    if (hints?.isReview) return 'review';
    if (hints?.isSearch) return 'search';
    if (hints?.isBookmarks) return 'bookmark';
    if (hints?.fromSeedSlug) return 'shared_link';
    if (hints?.hasCategoryFilter) return 'category_feed';

    if (typeof window === 'undefined') {
        return 'direct';
    }

    const path = window.location.pathname;
    if (path.startsWith('/bookmarks')) return 'bookmark';

    const params = new URLSearchParams(window.location.search);
    if (params.get('category') || params.get('subcategory') || params.get('category_id')) {
        return 'category_feed';
    }
    if (path === '/content') return 'category_feed';
    if (path === '/' || path.startsWith('/q/')) return 'recommended_feed';
    return 'direct';
}

export function resolveCategorySlug(input: {
    category?: string | null;
    subcategory?: string | null;
    tagSlugs?: Array<string | null | undefined> | null;
}): string | undefined {
    const subcategory = (input.subcategory || '').trim();
    if (subcategory) return subcategory;
    const category = (input.category || '').trim();
    if (category && !/^\d+$/.test(category)) return category;
    for (const raw of input.tagSlugs ?? []) {
        const slug = (raw || '').trim();
        if (slug) return slug;
    }
    return undefined;
}

function hasTrackedContentView(contentKey: string): boolean {
    try {
        return sessionStorage.getItem(`${GA_CONTENT_VIEW_PREFIX}${contentKey}`) !== null;
    } catch {
        return false;
    }
}

function markTrackedContentView(contentKey: string): void {
    try {
        sessionStorage.setItem(`${GA_CONTENT_VIEW_PREFIX}${contentKey}`, '1');
    } catch {
        // ignore
    }
}

export function markContentEngagementStart(contentId: number): void {
    if (!engagementStartedAt.has(contentId)) {
        engagementStartedAt.set(contentId, Date.now());
    }
}

export function takeContentEngagementDurationMs(contentId: number): number | undefined {
    const started = engagementStartedAt.get(contentId);
    engagementStartedAt.delete(contentId);
    if (started == null) return undefined;
    return Math.max(0, Date.now() - started);
}

// --- Convenience trackers ----------------------------------------------------

export function trackSignUp(method: AuthMethod): void {
    trackEvent('sign_up', { method });
}

export function trackLogin(method: AuthMethod): void {
    trackEvent('login', { method });
}

export type ContentAnalyticsFields = {
    contentType: ContentType;
    category?: string;
    difficulty?: ContentDifficultyLabel | string;
    source: ContentSource;
};

export function trackContentView(
    fields: ContentAnalyticsFields & { contentKey?: string | number }
): void {
    const key = String(fields.contentKey ?? `${fields.contentType}:${fields.category ?? ''}:${fields.source}`);
    if (hasTrackedContentView(key)) {
        return;
    }
    markTrackedContentView(key);
    if (typeof fields.contentKey === 'number') {
        markContentEngagementStart(fields.contentKey);
    }
    trackEvent('content_view', {
        content_type: fields.contentType,
        category: fields.category,
        difficulty: fields.difficulty,
        source: fields.source,
    });
}

export function trackContentComplete(
    fields: ContentAnalyticsFields & {
        success: boolean;
        durationMs?: number;
        contentKey?: number;
    }
): void {
    const durationMs =
        fields.durationMs ??
        (typeof fields.contentKey === 'number'
            ? takeContentEngagementDurationMs(fields.contentKey)
            : undefined) ??
        0;

    trackEvent('content_complete', {
        content_type: fields.contentType,
        category: fields.category,
        difficulty: fields.difficulty,
        source: fields.source,
        success: fields.success,
        duration_bucket: toDurationBucket(durationMs),
    });
}

export function trackBookmark(
    fields: Pick<ContentAnalyticsFields, 'contentType' | 'category' | 'source'>
): void {
    trackEvent('bookmark', {
        content_type: fields.contentType,
        category: fields.category,
        source: fields.source,
    });
}

export function trackBookmarkRemove(
    fields: Pick<ContentAnalyticsFields, 'contentType' | 'category' | 'source'>
): void {
    trackEvent('bookmark_remove', {
        content_type: fields.contentType,
        category: fields.category,
        source: fields.source,
    });
}

export function trackReaction(
    fields: Pick<ContentAnalyticsFields, 'contentType' | 'category'> & {
        reactionType: ReactionType;
    }
): void {
    trackEvent('reaction', {
        content_type: fields.contentType,
        category: fields.category,
        reaction_type: fields.reactionType,
    });
}

export function trackShare(
    fields: Pick<ContentAnalyticsFields, 'contentType' | 'category'> & {
        method: ShareMethod;
    }
): void {
    trackEvent('share', {
        method: fields.method,
        content_type: fields.contentType,
        category: fields.category,
    });
}

export function trackLevelUp(level: number): void {
    trackEvent('level_up', { level });
}

export function trackAchievement(achievementId: string): void {
    const id = achievementId.trim();
    if (!id) return;
    trackEvent('unlock_achievement', { achievement_id: id });
}

export function trackContentSubmit(
    fields: Pick<ContentAnalyticsFields, 'contentType' | 'category'>
): void {
    trackEvent('content_submit', {
        content_type: fields.contentType,
        category: fields.category,
    });
}

/** Object-style API preferred by call sites. */
export const analytics = {
    trackEvent,
    trackPageView,
    trackSignUp,
    trackLogin,
    trackContentView,
    trackContentComplete,
    trackBookmark,
    trackBookmarkRemove,
    trackReaction,
    trackShare,
    trackLevelUp,
    trackAchievement,
    trackContentSubmit,
    toDurationBucket,
    toDifficultyLabel,
    resolveContentType,
    resolveContentSource,
    resolveCategorySlug,
    markContentEngagementStart,
    takeContentEngagementDurationMs,
};

export default analytics;
